<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/product/ProductPrice.vue
// IDS-134: add minimal layout
// IDS-280: extend product price component with list price display options for price transparency
import type { Schemas } from '@shopware/api-client/api-types';

const props = withDefaults(
    defineProps<{
        product: Schemas['Product'];
        listPriceDisplay?: 'none' | 'percentage' | 'value';
        fontSize?: 'text-xs' | 'text-sm' | 'text-base' | 'text-lg' | 'text-xl' | 'text-2xl' | 'text-3xl';
        fontWeight?:
            | 'font-thin'
            | 'font-light'
            | 'font-normal'
            | 'font-medium'
            | 'font-semibold'
            | 'font-bold'
            | 'font-extrabold'
            | 'font-black';
        displayShortVat?: boolean;
        displayLongVat?: boolean;
        displayPriceInfo?: boolean;
        layout?: 'standard' | 'minimal';
        showListPriceOnTop?: boolean;
        msrpTextClass?: string;
        msrpPriceClass?: string;
    }>(),
    {
        // CUSTOM IDS-95: changed all styling
        listPriceDisplay: 'none',
        fontSize: 'text-lg',
        fontWeight: 'font-extrabold',
        displayShortVat: false,
        displayLongVat: false,
        displayPriceInfo: false,
        layout: 'standard',
        showListPriceOnTop: false,
        msrpTextClass: 'text-base mr-2 text-neutral-600',
        msrpPriceClass: 'text-3xl font-extrabold text-neutral-600',
    },
);

const product = ref(props.product);
const customerStore = useCustomerStore();
const { signedIn } = storeToRefs(customerStore);
const { t } = useI18n();

const showPrice = computed(() => {
    return (
        signedIn.value ||
        (
            !signedIn.value &&
            product.value.customFields?.priceTransparency !== 'HIDE_PRICE' &&
            product.value.translated.customFields?.priceAvailable
        )
    );
});

const showMSRP = computed(() => {
    return (
        !signedIn.value &&
        product.value.customFields?.priceTransparency === 'SHOW_MSRP' &&
        product.value.customFields?.msrp
    );
});

const { getFormattedPrice } = usePrice();
const { price, unitPrice, isListPrice, referencePrice } = useProductPrice(product);
</script>

<template>
    <div>
        <template v-if="showPrice">
            <p
                :class="{
                    grid: showListPriceOnTop,
                }"
            >
                <!--CUSTOM IDS-95: changed color and added additional styling-->
                <span
                    v-if="unitPrice && isListPrice && !showMSRP"
                    :class="[
                        fontSize,
                        fontWeight,
                        {
                            'text-brand-secondary': isListPrice,
                            '!font-bold': layout === 'minimal',
                        },
                    ]"
                    class="non-italic font-main leading-snug"
                >
                    <template v-if="layout !== 'minimal'">
                        {{ t('product.priceLabel', { unitPrice: getFormattedPrice(unitPrice) }) }}
                    </template>
                    <template v-else>
                        {{ getFormattedPrice(unitPrice) }}
                    </template>
                </span>
                <span
                    v-else-if="unitPrice && !isListPrice && !showMSRP"
                    :class="[
                        fontSize,
                        fontWeight,
                        { 'text-brand-neutral-black': !isListPrice },
                    ]"
                    class="non-italic font-main leading-snug"
                >
                    {{ getFormattedPrice(unitPrice) }}
                </span>

                <template v-if="isListPrice && !showMSRP">
                    <!--CUSTOM IDS-95: changed styling-->
                    <span
                        class="non-italic ml-1.5 font-main text-sm font-normal leading-5 text-gray line-through"
                        :class="{
                            'text-xs': layout === 'minimal',
                        }"
                    >
                        {{ getFormattedPrice(price?.listPrice.price) }}

                        <template v-if="listPriceDisplay === 'percentage'">
                            (-{{ price?.listPrice.percentage }}%)
                        </template>

                        <template v-else-if="listPriceDisplay === 'value'">
                            {{ getFormattedPrice(price?.listPrice.discount) }}
                        </template>
                    </span>
                </template>

                <span
                    v-if="showPrice && showMSRP"
                    class="w-full">
                <span
                    v-if="showMSRP"
                    :class="msrpTextClass"
                >{{ t('product.priceMSRP') }}
                    </span>
                    <span :class="msrpPriceClass">{{ getFormattedPrice(unitPrice) }}</span>
                </span>
            </p>
            <p
                v-if="referencePrice && !showMSRP"
                class="w-full text-sm text-gray"
            >
                {{ referencePrice.purchaseUnit }} {{ referencePrice.unitName }} ({{
                    getFormattedPrice(referencePrice.price)
                }}/{{ referencePrice.referenceUnit }} {{ referencePrice.unitName }})
            </p>
            <!-- IDS-84 - add different translations for vat-->
            <p
                v-if="showPrice && !showMSRP && displayLongVat"
                class="w-full text-sm text-neutral-600"
            >
                {{ t('productIds.price.vatInfoLong') }}
            </p>

            <p
                v-else-if="showPrice && !showMSRP && displayShortVat"
                class="w-full text-sm text-neutral-600"
            >
                {{ t('productIds.price.vatInfoShort') }}
            </p>
            <span
                v-else-if="showPrice && showMSRP && displayPriceInfo"
                class="w-full text-sm text-neutral-600"
            >
                <b>{{ t('product.priceMSRPInfoBold') }}</b> {{ t('product.priceMSRPInfo') }}
            </span>
        </template>
        <template v-else>
            <slot name="priceHidden" />
            <template v-if="!showPrice && displayPriceInfo">
                <br />
                <span class="w-full text-sm text-neutral-600">
                    <b>{{ t('product.priceLoginInfoBold') }}</b> {{ t('product.priceLoginInfo') }}
                </span>
            </template>
        </template>
    </div>
</template>
